import { ErrorCode } from '@constants/errorCodes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { getApiErrorMessage } from '@helpers/apiError';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';

import { MoveToCofeeAction } from './constants';
import {
  getNameFieldErrorMessage,
  parseMoveToCofeeResponse
} from './responseHelpers';
import {
  AddToMemberListFormData,
  QuickCollectFormData,
  QuickCollectGroupFormData
} from './types';

import { getDefaultBranchMember } from '../../helper';
import {
  useLeadAddToBranchMutation,
  useLeadAddToGroupMutation,
  useLeadQuickCollectMutation
} from '../../queries/LeadManagementQuery';
import { LeadType } from '../../types/common';

const getCapitalizedApiErrorMessage = (error: unknown): string | undefined => {
  const message = getApiErrorMessage(error);
  return message ? capitalizeFirstLetter(message) : undefined;
};

const getAddMemberErrorMessage = (error: unknown, fallback: string): string => {
  const errorCode = (error as ApiError)?.data?.errorCode;
  if (errorCode === ErrorCode.ERR_MEMBER_ALREADY_EXISTS) {
    return 'This lead already exist in the selected branch.';
  }
  return getCapitalizedApiErrorMessage(error) || fallback;
};

interface UseMoveToCofeeSubmitArgs {
  leadData: LeadType;
  selectedAction: MoveToCofeeAction | null;
  memberListForm: AddToMemberListFormData;
  quickCollectForm: QuickCollectFormData;
  quickCollectGroupForm: QuickCollectGroupFormData;
  onClose: () => void;
  onSuccess: () => void;
  onNameError: (message: string) => void;
}

const useMoveToCofeeSubmit = ({
  leadData,
  selectedAction,
  memberListForm,
  quickCollectForm,
  quickCollectGroupForm,
  onClose,
  onSuccess,
  onNameError
}: UseMoveToCofeeSubmitArgs) => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { createSuccessAlert, createErrorAlert } = useNotify();

  // Notify the parent that the move succeeded so it can optimistically mark the
  // lead as onboarded, then alert and close.
  const handleSuccess = (message: string) => {
    onSuccess();
    createSuccessAlert(message);
    onClose();
  };

  const handleApiError = (message?: string) => {
    createErrorAlert(message);
    onClose();
  };

  const handleApiFailure = (error: unknown, fallbackMessage: string) => {
    const nameMessage = getNameFieldErrorMessage(error);
    if (nameMessage) {
      onNameError(nameMessage);
      return;
    }
    handleApiError(fallbackMessage);
  };

  const handleApiResult = (
    responseBody: unknown,
    successMessage: string,
    fallbackErrorMessage: string
  ) => {
    const result = parseMoveToCofeeResponse(responseBody);
    if (result.type === 'success') {
      handleSuccess(successMessage);
    } else if (result.type === 'nameError') {
      onNameError(result.message);
    } else {
      handleApiError(result.message || fallbackErrorMessage);
    }
  };

  const [leadAddToBranch, { isLoading: isAddingToBranch }] =
    useLeadAddToBranchMutation();
  const [leadAddToGroup, { isLoading: isAddingToGroup }] =
    useLeadAddToGroupMutation();
  const [leadQuickCollect, { isLoading: isQuickCollecting }] =
    useLeadQuickCollectMutation();

  const isSubmitting = isAddingToBranch || isAddingToGroup || isQuickCollecting;

  const submitAddToMemberList = async () => {
    const branchMember = getDefaultBranchMember(
      memberListForm.name,
      leadData.mobile_number,
      leadData.country_code
    );

    if (memberListForm.assignToGroup && memberListForm.groupId) {
      const res = await leadAddToGroup({
        params: {
          organizationId,
          branchId: memberListForm.branchId,
          groupId: memberListForm.groupId,
          leadId: leadData.id
        },
        body: {
          members_added: [
            {
              name: memberListForm.name,
              mobile: leadData.mobile_number,
              country_code: leadData.country_code.replace('+', ''),
              amount: '0'
            }
          ],
          force_create: true
        }
      });
      if ('data' in res) {
        handleApiResult(
          res.data,
          'Lead added to group successfully',
          'Failed to add lead to group'
        );
      } else {
        handleApiFailure(
          res.error,
          getAddMemberErrorMessage(res.error, 'Failed to add lead to group')
        );
      }
    } else {
      const res = await leadAddToBranch({
        params: {
          organizationId,
          branchId: memberListForm.branchId,
          leadId: leadData.id
        },
        body: {
          branch_members: [branchMember],
          force_create: true
        }
      });
      if ('data' in res) {
        handleApiResult(
          res.data,
          'Lead added to branch successfully',
          'Failed to add lead to branch'
        );
      } else {
        handleApiFailure(
          res.error,
          getAddMemberErrorMessage(res.error, 'Failed to add lead to branch')
        );
      }
    }
  };

  const submitQuickCollect = async () => {
    const res = await leadQuickCollect({
      organizationId,
      leadId: leadData.id,
      branchId: quickCollectForm.branchId,
      body: {
        amount: +quickCollectForm.amount,
        note: quickCollectForm.notes.trim(),
        member_details: [
          {
            name: quickCollectForm.name,
            mobile: leadData.mobile_number,
            country_code: leadData.country_code.replace('+', '')
          }
        ],
        send_payment_links: true
      }
    });
    if ('data' in res) {
      handleApiResult(
        res.data,
        'Payment link sent successfully',
        'Failed to send payment link'
      );
    } else {
      handleApiFailure(
        res.error,
        getCapitalizedApiErrorMessage(res.error) ||
          'Failed to send payment link'
      );
    }
  };

  const submitQuickCollectGroup = async () => {
    const form = quickCollectGroupForm;
    const quickCollectBody: Record<string, any> = {
      amount: +form.amount,
      note: form.notes.trim(),
      member_details: [
        {
          name: form.name,
          mobile: leadData.mobile_number,
          country_code: leadData.country_code.replace('+', '')
        }
      ],
      send_payment_links: true,
      group_id: form.groupId
    };

    const res = await leadQuickCollect({
      organizationId,
      leadId: leadData.id,
      branchId: form.branchId,
      body: quickCollectBody
    });
    if ('data' in res) {
      handleApiResult(
        res.data,
        'Payment link sent successfully',
        'Failed to send payment link'
      );
    } else {
      handleApiFailure(
        res.error,
        getCapitalizedApiErrorMessage(res.error) ||
          'Failed to send payment link'
      );
    }
  };

  const handleSubmit = () => {
    if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
      submitAddToMemberList();
    } else if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
      submitQuickCollect();
    } else if (
      selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP
    ) {
      submitQuickCollectGroup();
    }
  };

  return { handleSubmit, isSubmitting };
};

export default useMoveToCofeeSubmit;
